/**
 * variant-explosion.js — v14
 * Ritual Theme 3.4.0 — Variant Explosion Feature
 *
 * ARCHITECTURE (confirmed against live DOM, Feb 2026):
 *
 * On-load explosion:
 *   - Reads seed JSON from each [data-seed-variant-id] card
 *   - Writes price/badge/hover-image/metafields directly from seed data
 *   - Fires swatch click to update URL and Quick Add button state
 *
 * Post-explosion swatch click (user interaction):
 *   - Native SwatchesVariantPickerComponent fires Section Rendering API call
 *   - BUT: ProductPrice.updatePrice fails silently because the section rendering
 *     response product-price data-block-id doesn't match the card's block-id
 *     (each card has a unique suffix: -1, -2, -3... but response always has -1)
 *   - FIX: Listen for 'change' events on exploded cards, fetch the section rendering
 *     response ourselves using swatches-variant-picker-component[data-product-url],
 *     and write price/badge directly from the response HTML
 *
 * Key discovery: product URL comes from swatches-variant-picker-component[data-product-url],
 * NOT from variant-picker[data-product-url] (variant-picker has no dataset in collection cards)
 */

(function () {
  'use strict';

  // ─── Styles ────────────────────────────────────────────────────────────────

  function injectStyles() {
    if (document.getElementById('explosion-styles')) return;
    const style = document.createElement('style');
    style.id = 'explosion-styles';
    style.textContent = `
      .explosion-promo {
        display: block;
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--color-primary, #e00);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-top: 2px;
      }
      .explosion-sustainability {
        display: block;
        font-size: 0.7rem;
        font-weight: 500;
        color: var(--color-foreground, #333);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin-top: 4px;
        opacity: 0.75;
      }
    `;
    document.head.appendChild(style);
  }

  // ─── Seed parsing ──────────────────────────────────────────────────────────

  function readSeed(li) {
    const tag = li.querySelector('script[data-explosion-seed]');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent);
    } catch (e) {
      console.warn('[variant-explosion v14] Failed to parse seed JSON', e);
      return null;
    }
  }

  // ─── Price injection ───────────────────────────────────────────────────────

  function injectPrice(li, seed) {
    const priceEl = li.querySelector('product-price');
    if (!priceEl) return;

    const container = priceEl.querySelector('[ref="priceContainer"]');
    if (!container) return;

    if (seed.onSale && seed.comparePrice) {
      container.innerHTML = `
        <span role="group">
          <span class="visually-hidden">Sale price&nbsp;</span>
          <span class="price">${seed.price} USD</span>
        </span>
        <span role="group">
          <span class="visually-hidden">Regular price&nbsp;</span>
          <span class="compare-at-price">${seed.comparePrice} USD</span>
        </span>
      `;
    } else {
      container.innerHTML = `<span class="price">${seed.price} USD</span>`;
    }
  }

  // ─── Price update from Section Rendering response ─────────────────────────

  function updatePriceFromResponse(li, responseDoc) {
    const responsePriceContainer = responseDoc.querySelector('[ref="priceContainer"]');
    if (!responsePriceContainer) return;

    // Re-query live DOM after fetch (morph may have replaced elements)
    const livePriceContainer = li.querySelector('[ref="priceContainer"]');
    if (!livePriceContainer) return;

    livePriceContainer.innerHTML = responsePriceContainer.innerHTML;
  }

  // ─── Badge injection ───────────────────────────────────────────────────────

  function injectBadge(li, seed) {
    const badgeContainer = li.querySelector('[ref="badgeContainer"]');
    if (!badgeContainer) return;

    let badgeLabel = '';
    if (seed.badgeOverride) {
      badgeLabel = seed.badgeOverride;
    } else if (seed.onSale) {
      badgeLabel = 'Sale';
    }

    let badgeEl = badgeContainer.querySelector('.product-badges__badge');

    if (!badgeLabel) {
      if (badgeEl) badgeEl.style.display = 'none';
      return;
    }

    if (badgeEl) {
      badgeEl.style.display = '';
      badgeEl.textContent = badgeLabel;
    } else {
      const saleScheme = badgeContainer.dataset.saleColorScheme || '5';
      badgeEl = document.createElement('div');
      badgeEl.className = `product-badges__badge product-badges__badge--rectangle color-scheme-${saleScheme}`;
      badgeEl.textContent = badgeLabel;
      badgeContainer.appendChild(badgeEl);
    }
  }

  // ─── Badge update from Section Rendering response ─────────────────────────

  function updateBadgeFromResponse(li, responseDoc) {
    const hasSalePrice = !!responseDoc.querySelector('.price--sale') ||
                         responseDoc.body?.innerHTML?.includes('Sale price');

    const badgeContainer = li.querySelector('[ref="badgeContainer"]');
    if (!badgeContainer) return;

    let badgeEl = li.querySelector('.product-badges__badge');

    if (hasSalePrice) {
      if (!badgeEl) {
        const saleScheme = badgeContainer.dataset.saleColorScheme || '5';
        badgeEl = document.createElement('div');
        badgeEl.className = `product-badges__badge product-badges__badge--rectangle color-scheme-${saleScheme}`;
        badgeEl.textContent = 'Sale';
        badgeContainer.appendChild(badgeEl);
      }
      badgeEl.style.display = '';
    } else {
      if (badgeEl) badgeEl.style.display = 'none';
    }
  }

  // ─── Hover image injection ────────────────────────────────────────────────
  //
  // The native ProductCard.previewImage calls slideshow.next() on hover —
  // it shows whatever slide immediately follows the currently-visible slide.
  // We need the hover image to be that next slide.
  //
  // Two cases:
  //   A) Hover image already exists as a <slideshow-slide> in the DOM
  //      (it's in the product's Shopify media gallery) → move it to be next
  //   B) Hover image is a metafield image not in the media gallery
  //      → clone the visible slide, update its img src, insert after visible slide

  function injectHoverImage(li, seed) {
    if (!seed.hoverImageUrl) return;

    const slideshow = li.querySelector('slideshow-slides');
    if (!slideshow) return;

    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
    if (!slides.length) return;

    // Find the currently-visible slide (aria-hidden=false)
    const visibleSlide = slides.find(s => s.getAttribute('aria-hidden') === 'false');
    if (!visibleSlide) return;

    // Extract just the filename from the hover URL for matching
    const hoverFilename = seed.hoverImageUrl.split('/').pop().split('?')[0];

    // Case A: hover slide already exists in the slideshow
    const existingHoverSlide = slides.find(s => {
      const img = s.querySelector('img');
      return img && (img.src.includes(hoverFilename) || (img.srcset && img.srcset.includes(hoverFilename)));
    });

    if (existingHoverSlide) {
      // Move it to immediately after the visible slide (if not already there)
      if (visibleSlide.nextElementSibling !== existingHoverSlide) {
        visibleSlide.insertAdjacentElement('afterend', existingHoverSlide);
      }
      return;
    }

    // Case B: hover image is a metafield image, not in the slideshow
    // Check if we already injected one (avoid duplicates on re-run)
    const alreadyInjected = visibleSlide.nextElementSibling?.dataset?.explosionHover === hoverFilename;
    if (alreadyInjected) return;

    // Clone the visible slide as a template
    const hoverSlide = visibleSlide.cloneNode(true);
    hoverSlide.setAttribute('aria-hidden', 'true');
    hoverSlide.removeAttribute('variant-image'); // not a variant image
    hoverSlide.removeAttribute('slide-id');      // avoid ID collision
    hoverSlide.dataset.explosionHover = hoverFilename; // mark for dedup check

    const img = hoverSlide.querySelector('img');
    if (img) {
      img.src = seed.hoverImageUrl;
      img.srcset = seed.hoverImageUrl;
      img.removeAttribute('loading');
      img.alt = `#color_${seed.color || ''} hover`;
    }

    visibleSlide.insertAdjacentElement('afterend', hoverSlide);
  }

  // ─── Metafield text injection ─────────────────────────────────────────────

  function injectMetafields(li, seed) {
    const productPrice = li.querySelector('product-price');
    if (productPrice) {
      let promoEl = li.querySelector('.explosion-promo');
      if (!promoEl) {
        promoEl = document.createElement('div');
        promoEl.className = 'explosion-promo';
        productPrice.insertAdjacentElement('afterend', promoEl);
      }
      promoEl.textContent = seed.promoMessage || '';
      promoEl.style.display = seed.promoMessage ? '' : 'none';
    }

    const productSwatches = li.querySelector('product-swatches');
    if (productSwatches) {
      let sustainEl = li.querySelector('.explosion-sustainability');
      if (!sustainEl) {
        sustainEl = document.createElement('div');
        sustainEl.className = 'explosion-sustainability';
        productSwatches.insertAdjacentElement('afterend', sustainEl);
      }
      sustainEl.textContent = seed.sustainabilityAttributes || '';
      sustainEl.style.display = seed.sustainabilityAttributes ? '' : 'none';
    }
  }

  // ─── Swatch click (URL + Quick Add state only) ────────────────────────────

  function triggerSwatchClick(li, seed) {
    const swatchInput = li.querySelector(`input[data-variant-id="${seed.variantId}"]`);
    if (!swatchInput || swatchInput.checked) return;

    swatchInput.checked = true;
    swatchInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ─── Post-interaction swatch change handler ───────────────────────────────
  // When user clicks a swatch on an exploded card, the native morph doesn't update
  // price because the section rendering response data-block-id doesn't match the
  // card's unique block-id suffix. We handle price/badge ourselves.

  function attachSwatchChangeListener(li) {
    li.addEventListener('change', async (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

      const optionValueId = input.dataset.optionValueId;
      if (!optionValueId) return;

      // Get product URL from swatches-variant-picker-component (NOT variant-picker)
      const svpc = li.querySelector('swatches-variant-picker-component');
      const productUrl = svpc?.dataset?.productUrl;
      if (!productUrl) return;

      const url = `${productUrl}?section_id=section-rendering-product-card&option_values=${optionValueId}`;

      try {
        const resp = await fetch(url);
        const text = await resp.text();
        const parser = new DOMParser();
        const responseDoc = parser.parseFromString(text, 'text/html');

        // Update price and badge from response HTML
        updatePriceFromResponse(li, responseDoc);
        updateBadgeFromResponse(li, responseDoc);
      } catch (e) {
        console.warn('[variant-explosion v14] Swatch price update failed:', e);
      }
    });
  }

  // ─── Core: process one exploded card ──────────────────────────────────────

  function processCard(li) {
    const seed = readSeed(li);
    if (!seed || !seed.variantId) return;

    // Mark as processed to avoid double-firing (infinite scroll)
    if (li.dataset.explosionSeeded === seed.variantId) return;
    li.dataset.explosionSeeded = seed.variantId;

    // 1. Write price directly from seed (correct per-variant price)
    injectPrice(li, seed);

    // 2. Write badge directly from seed (correct per-variant sale/override state)
    injectBadge(li, seed);

    // 3. Inject hover image
    injectHoverImage(li, seed);

    // 4. Inject metafield text (promo, sustainability)
    injectMetafields(li, seed);

    // 5. Attach swatch change listener for post-interaction price/badge updates
    attachSwatchChangeListener(li);

    // 6. Fire swatch click to update URL and Quick Add button state
    //    Small delay to avoid race with price writes above
    setTimeout(() => triggerSwatchClick(li, seed), 50);
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    // Don't run in Theme Editor — would interfere with editor state
    if (window.Shopify && window.Shopify.designMode) return;
    document.querySelectorAll('[data-seed-variant-id]').forEach(processCard);
  }

  injectStyles();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Infinite scroll support
  const gridObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.hasAttribute('data-seed-variant-id')) {
          processCard(node);
        } else {
          node.querySelectorAll('[data-seed-variant-id]').forEach(processCard);
        }
      }
    }
  });

  const grid = document.querySelector('.product-grid, [id*="product-grid"]');
  if (grid) gridObserver.observe(grid, { childList: true, subtree: true });

})();
